import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { getConsejoMiembros, saveConsejoMiembro, deleteConsejoMiembro } from '../service/ComunidadService';

const HOST_API = process.env.REACT_APP_URL;

const CARGOS = ['Presidente', 'Secretario', 'Tesorero', 'Vocal', 'Vocal Suplente'];

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const FORM_EMPTY = { consejoMiembroId: 0, personaId: null, cargo: '', fechaDesde: new Date(), fechaHasta: null };

const ComunidadConsejoAdmin = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [miembros, setMiembros] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ ...FORM_EMPTY });
    const [submitting, setSubmitting] = useState(false);

    const fetchMiembros = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getConsejoMiembros(administracionId, barrio.id, false);
            setMiembros(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el consejo', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchMiembros(); }, [fetchMiembros]);

    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/personas/GetPersonasCombo`, { params: { administracionId, barrioId: barrio.id } })
            .then(({ data }) => setPersonas(data))
            .catch(() => {});
    }, [barrio?.id, administracionId]);

    const abrirNuevo = () => { setForm({ ...FORM_EMPTY }); setModalVisible(true); };
    const editar = (row) => {
        setForm({
            ...row,
            fechaDesde: new Date(row.fechaDesde),
            fechaHasta: row.fechaHasta ? new Date(row.fechaHasta) : null,
        });
        setModalVisible(true);
    };

    const guardar = async () => {
        if (!form.personaId || !form.cargo || !form.fechaDesde) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Completá persona, cargo y fecha desde', life: 3000 });
            return;
        }
        setSubmitting(true);
        try {
            await saveConsejoMiembro({ ...form, barrioId: barrio.id, administracionId });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Miembro guardado correctamente', life: 3000 });
            setModalVisible(false);
            fetchMiembros();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el miembro', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const eliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar a ${row.nombreCompleto} del consejo?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteConsejoMiembro(row.consejoMiembroId, administracionId);
                    fetchMiembros();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el miembro', life: 3000 });
                }
            },
        });
    };

    return (
        <>
            <h4>Consejo</h4>
            <br />
            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nuevo Miembro" icon="pi pi-plus" className="p-button-sm" onClick={abrirNuevo} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={miembros} paginator rows={15}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay miembros cargados"
                        className="p-datatable-gridlines bg-white">
                        <Column field="nombreCompleto" header="Persona" sortable />
                        <Column field="cargo" header="Cargo" sortable style={{ width: '12rem' }} />
                        <Column header="Desde" body={(r) => fmtFecha(r.fechaDesde)} style={{ width: '9rem' }} />
                        <Column header="Hasta" body={(r) => r.fechaHasta ? fmtFecha(r.fechaHasta) : 'Vigente'} style={{ width: '9rem' }} />
                        <Column header="Acciones" style={{ width: '8rem', textAlign: 'center' }} body={(row) => (
                            <div className="flex gap-2 justify-content-center">
                                <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm" severity="warning" onClick={() => editar(row)} />
                                <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger" onClick={() => eliminar(row)} />
                            </div>
                        )} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={form.consejoMiembroId ? 'Editar Miembro' : 'Nuevo Miembro'} visible={modalVisible} style={{ width: '460px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardar} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>Persona *</label>
                        <Dropdown value={form.personaId} options={personas} optionLabel="nombreCompleto" optionValue="personaId"
                            filter placeholder="Seleccionar persona" onChange={(e) => setForm(f => ({ ...f, personaId: e.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Cargo *</label>
                        <Dropdown value={form.cargo} options={CARGOS} placeholder="Seleccionar cargo" onChange={(e) => setForm(f => ({ ...f, cargo: e.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Desde *</label>
                        <Calendar value={form.fechaDesde} onChange={(e) => setForm(f => ({ ...f, fechaDesde: e.value }))} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Hasta</label>
                        <Calendar value={form.fechaHasta} onChange={(e) => setForm(f => ({ ...f, fechaHasta: e.value }))} dateFormat="dd/mm/yy" showIcon showButtonBar className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default ComunidadConsejoAdmin;
